import {
  Column,
  ColumnOptions as GrecoColumnOptions,
} from "library/greco-components";
import { getColumnGroupings } from "./columnGroupings";
import { useAddAppSettings } from "hooks/data/mutations/useAddAppSettings";
import { useDeleteAppSettings } from "hooks/data/mutations/useDeleteAppSettings";
import { useUpdateAppSettings } from "hooks/data/mutations/useUpdateAppSettings";
import { useGetInsurerListByCountry } from "hooks/data/queries/useGetInsurerListByCountry";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setSelectedViewIdRav } from "store/requireActionVehicles";
import { Setting } from "types/types";
import {
  selectColumnsSelectedView,
  selectSelectedView,
  setSelectedViewIdFleet,
} from "../../store/fleetPage";
import {
  clearAllFilters,
  hideAllColumns,
  markFleetPageColumnsSaved,
  reorderColumn,
  selectAllFleetPageColumns,
  selectAllFleetPageTempColumns,
  selectFleetPageColumnsDirty,
  setFilter,
  setFleetPageColumns,
  setFleetPageTempColumns,
  showAllColumns,
  toggleColumnVisibility,
  updateFleetPageColumns,
} from "../../store/fleetPageColumnOptions";
import { useSelector } from "../../store/hooks";
import {
  selectColumnOptionsSettings,
  setAllSettings,
} from "../../store/settings";
import { AppDispatch } from "../../store/store";

type Props = {
  closePanel: () => void;
  countryCode: number | undefined;
};

export const ColumnOptions = ({ closePanel, countryCode }: Props) => {
  const fleetPageTempColumns = useSelector(selectAllFleetPageTempColumns);
  const fleetPageColumns = useSelector(selectAllFleetPageColumns);
  const dispatch: AppDispatch = useDispatch();

  // Close discards unapplied edits: reset the temp columns back to the
  // currently applied (displayed) columns so reopening shows the live view.
  const handleClose = () => {
    dispatch(setFleetPageTempColumns(fleetPageColumns));
    closePanel();
  };
  const selectedView = useSelector(selectSelectedView) as any;
  const selectedViewColumns = useSelector(selectColumnsSelectedView) as any;
  const taxonomy = useSelector((s) => s.taxonomy);
  const applicationTax = taxonomy.ApplicationCode.byCode["Application.BFM"];
  const columnOptionsTax =
    taxonomy.ApplicationSetting.byCode["AppSetting.ColumnOptions"];
  const columnOptionsSettings = useSelector(selectColumnOptionsSettings);
  const areSettingsLoading = useSelector((s) => s.settings.isLoading);
  const areSettingsLoaded = useSelector((s) => s.settings.isLoaded);
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);

  const { t } = useTranslation();

  const groupings = useMemo(() => getColumnGroupings(t), [t]);

  // Dirty when a view is selected and the user has made edits (visibility,
  // order or filters) that haven't been saved. Tracked in the store so it only
  // reflects real user actions, not representation differences between the
  // working columns and the freshly re-parsed view JSON. Switching/reloading a
  // view resets the flag (saga reloads temp columns -> setTempColumns).
  const isDirty = useSelector(selectFleetPageColumnsDirty);
  const isViewDirty = !!selectedView && isDirty;

  const { mutate: addAppSetting, isLoading: isLoadingCreate } =
    useAddAppSettings();

  const { mutate: updateAppSetting, isLoading: isLoadingUpdate } =
    useUpdateAppSettings();

  const { mutate: deleteAppSetting, isLoading: isLoadingDelete } =
    useDeleteAppSettings();

  const { data: insurerList, isLoading: loadingInsurerList } =
    useGetInsurerListByCountry(countryCode);

  const searchInsurerTaxonomyData = insurerList?.map((item) => {
    return { code: item.insurerName, id: item.insurerInternalNumber };
  });

  const transformedColumns = useMemo(() => {
    const transformedColumns = fleetPageTempColumns.map((c) => {
      // Render PaymentMethod filters as a checkbox list (not a single-select
      // dropdown), even if a saved view stored an outdated isSelect flag.
      // Only the rendering flag is changed; the selected value is preserved.
      const filter =
        c.filter?.type === "taxonomy" &&
        c.filter?.taxonomyKey === "PaymentMethod" &&
        c.filter?.isSelect
          ? { ...c.filter, isSelect: false }
          : c.filter;

      if (c.key === "fleetName") {
        return {
          ...c,
          filter,
          label: t(c.labelKey),
          isPresentInColumnOptions: false,
        };
      }

      return {
        ...c,
        filter,
        label: t(c.labelKey),
      };
    });
    const fixedColumn = transformedColumns.filter((column) => column.isFixed);

    const unfixedTransformedColumns = transformedColumns.filter(
      (column) => !column.isFixed
    );

    return fixedColumn.concat(unfixedTransformedColumns);
  }, [t, fleetPageTempColumns]);

  const isViewLoading =
    areSettingsLoading ||
    isLoadingCreate ||
    isLoadingUpdate ||
    isLoadingDelete ||
    loadingInsurerList;

  if (!searchInsurerTaxonomyData) {
    return;
  }

  return (
    <GrecoColumnOptions
      t={t}
      groupings={groupings}
      hasUnsavedChanges={isViewDirty}
      columns={transformedColumns as Column[]}
      isReady={areSettingsLoaded}
      isViewLoading={isViewLoading}
      closePanel={handleClose}
      onChangeView={(id) => {
        dispatch(setSelectedViewIdFleet(id));
      }}
      onClickApply={() => {
        dispatch(updateFleetPageColumns(fleetPageTempColumns));
        closePanel();
      }}
      onClickClearAllFilters={() => {
        dispatch(clearAllFilters());
      }}
      onClickDeleteView={() => {
        deleteAppSetting(selectedView.userAppSettingId, {
          onSuccess: (res) => {
            dispatch(setAllSettings(res.data));
            dispatch(setSelectedViewIdFleet(null));
            dispatch(setSelectedViewIdRav(null));
          },
        });
      }}
      onClickReset={() => {
        dispatch(
          setFleetPageColumns({
            columns: selectedViewColumns,
            country: fleetAppSettings?.COUNTRY_CODE,
          })
        );
      }}
      onClickSaveView={() => {
        updateAppSetting(
          {
            ...selectedView,
            userAppSettingValue: JSON.stringify(
              fleetPageTempColumns.map((column) => {
                if (column.key === "insuranceLineIcons") {
                  return {
                    ...column,
                    minWidth: 320,
                    maxWidth: 400,
                  };
                }
                return column;
              })
            ),
          },
          {
            onSuccess: (res) => {
              dispatch(setAllSettings(res.data));
              dispatch(markFleetPageColumnsSaved());
            },
          }
        );
      }}
      onCreateView={(data) => {
        const applicationCodeTax =
          taxonomy.ApplicationCode.byCode["Application.BFM"];

        addAppSetting(
          {
            applicationCodeId: applicationTax.id,
            appSettingCodeId: columnOptionsTax.id,
            userAppSettingValue: JSON.stringify(fleetPageTempColumns),
            ...data,
          },
          {
            onSuccess: (res) => {
              const allSettings = res?.data as Setting[];
              const newSetting = allSettings.find((s) => {
                return (
                  s.applicationCodeId === applicationCodeTax?.id &&
                  s.userAppSettingName === data.userAppSettingName
                );
              });
              dispatch(setSelectedViewIdFleet(newSetting.userAppSettingId));
              dispatch(setSelectedViewIdRav(newSetting.userAppSettingId));
              dispatch(setAllSettings(allSettings));

              closePanel();
            },
          }
        );
      }}
      onUpdateView={(data) => {
        updateAppSetting(
          {
            ...selectedView,
            ...data,
          },
          {
            onSuccess: (res) => {
              dispatch(setAllSettings(res.data));
              closePanel();
            },
          }
        );
      }}
      onDeselectAll={() => {
        dispatch(hideAllColumns());
      }}
      onSelectAll={() => {
        dispatch(showAllColumns());
      }}
      onReorderColumn={(src, dest) => {
        dispatch(
          reorderColumn({
            index1: src,
            index2: dest,
          })
        );
      }}
      onToggleColumnVisibility={(columnKey) => {
        dispatch(toggleColumnVisibility(columnKey));
      }}
      selectedView={selectedView ? { ...selectedView } : null}
      setFilter={(columnKey, filter) => {
        dispatch(setFilter({ columnKey, filter }));
      }}
      taxonomy={{
        ...taxonomy.response,
        mtplInsurerPartnerNumber: [...searchInsurerTaxonomyData],
        hullInsurerPartnerNumber: [...searchInsurerTaxonomyData],
        fleetLegalInsurerPartnerNumber: [...searchInsurerTaxonomyData],
        hullOnLandInsurerPartnerNumber: [...searchInsurerTaxonomyData],
        machineryBreakdownInsurerPartnerNumber: [...searchInsurerTaxonomyData],
      }}
      views={columnOptionsSettings}
    />
  );
};
